<template>
  <div class="pagination">
    <a
      title="처음페이지"
      class="page_con first_page"
      :class="{ disabled: currentPage === 1 }"
      @click="setPage(1)"
      style="cursor: pointer"
    >
      처음페이지
    </a>

    <a
      @click="prevPage"
      class="page_con prev_page"
      :class="{ disabled: currentPage === 1 }"
      style="cursor: pointer"
    >
      이전페이지
    </a>

    <!-- 페이지 버튼 -->
    <a
      v-for="num in pageNumbers"
      :key="num"
      @click="setPage(num)"
      :class="{ active: currentPage === num }"
      style="cursor: pointer"
    >
      <p>{{ num }}</p>
    </a>

    <a
      title="다음페이지"
      class="page_con next_page"
      :class="{ disabled: currentPage === totalPages }"
      @click="nextPage"
      style="cursor: pointer"
    >
      다음페이지
    </a>
    <a
      title="마지막페이지"
      class="page_con last_page"
      :class="{ disabled: currentPage === totalPages }"
      @click="setPage(totalPages)"
      style="cursor: pointer"
    >
      마지막페이지
    </a>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { global } from "@/common/plugins/global";

/* ------------------------------------------------------------
                          Vue Instance & Global Properties
   ------------------------------------------------------------ */
const vm = global();

/* ------------------------------------------------------------
                          Model
   ------------------------------------------------------------ */
const serverPage = defineModel("page", { type: Number, required: false });
const api = defineModel("api", { type: Function, required: false });

/* ------------------------------------------------------------
                          Props
   ------------------------------------------------------------ */
const props = defineProps({
  serverOptions: {
    type: Object,
    required: true,
  },
});

/* ------------------------------------------------------------
                          Variable Declarations
   ------------------------------------------------------------ */
const currentPage = ref(1);

/* ------------------------------------------------------------
                          Computed Properties
   ------------------------------------------------------------ */
/** 전체 페이지 수 가져오기 */
const totalPages = computed(() => {
  return Math.ceil(props.serverOptions.totalCount / props.serverOptions.rowsPerPage);
});

/** 현재 페이지 기준으로 페이지 번호 배열 생성 */
const pageNumbers = computed(() => {
  let numbers = [];
  const maxPageNumDisplay = 5;

  if (totalPages.value <= maxPageNumDisplay) {
    // 전체 페이지가 5개 이하인 경우 모든 페이지 표시
    for (let i = 1; i <= totalPages.value; i++) {
      numbers.push(i);
    }
  } else {
    // 현재 페이지를 기준으로 5개 그룹 계산
    let startPage = Math.floor((currentPage.value - 1) / maxPageNumDisplay) * maxPageNumDisplay + 1;
    let endPage = Math.min(startPage + maxPageNumDisplay - 1, totalPages.value);

    for (let i = startPage; i <= endPage; i++) {
      numbers.push(i);
    }
  }

  return numbers;
});

/* ------------------------------------------------------------
                          Methods
   ------------------------------------------------------------ */
/** 페이지 설정 */
const setPage = (page: number) => {
  // 같은 페이지 클릭 시 API 호출 방지
  if (currentPage.value === page) {
    return;
  }

  currentPage.value = page;

  if (api.value) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const serverOpts = { ...props.serverOptions, page: currentPage.value } as any;
    const params = vm.$serial(serverOpts) as unknown as Record<string, unknown>;
    api.value(params);
  }
};

/** 이전 페이지 설정 */
const prevPage = () => {
  if (currentPage.value > 1) {
    setPage(currentPage.value - 1);
  }
};

/** 다음 페이지 설정 */
const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    setPage(currentPage.value + 1);
  }
};

/* ------------------------------------------------------------
                          watch
   ------------------------------------------------------------ */
/** 서버 페이지 변경 시 현재 페이지 설정 */
watch(serverPage, (val) => {
  if (currentPage.value) currentPage.value = Number(val);
});
</script>
